/*
  Read-aloud with the browser's speech synthesis. Never blocks input: speaking
  a new question cancels the last one, and nothing waits for speech to end.
  iPadOS only allows speech after a tap, so unlockSpeech() runs on the first
  tap of the session.
*/

export interface VoiceLike {
  lang: string;
  name: string;
  localService?: boolean;
  default?: boolean;
}

/** Voices that tend to sound best on Apple devices, in order of preference. */
const PREFERRED_NAMES = ["Karen", "Lee", "Catherine", "Gordon"];

/**
 * The best voice for an Australian child: en-AU first (preferring well-known
 * Apple voices, then on-device ones), then any English voice (en-GB before
 * others), else null, in which case read-aloud stays silent.
 */
export function chooseVoice<V extends VoiceLike>(voices: readonly V[]): V | null {
  const lang = (v: V) => v.lang.replace("_", "-").toLowerCase();
  const rank = (v: V): number => {
    const l = lang(v);
    let score =
      l === "en-au"
        ? 0
        : l === "en-gb"
          ? 100
          : l === "en-nz"
            ? 150
            : l.startsWith("en")
              ? 200
              : 1000;
    const named = PREFERRED_NAMES.findIndex((n) => v.name.includes(n));
    score += named >= 0 ? named : 10;
    if (!v.localService) score += 20;
    return score;
  };
  const english = voices.filter((v) => lang(v).startsWith("en"));
  if (!english.length) return null;
  return [...english].sort((a, b) => rank(a) - rank(b))[0]!;
}

function synth(): SpeechSynthesis | null {
  try {
    return typeof window !== "undefined" && "speechSynthesis" in window
      ? window.speechSynthesis
      : null;
  } catch {
    return null;
  }
}

let voice: SpeechSynthesisVoice | null | undefined;

function currentVoice(s: SpeechSynthesis): SpeechSynthesisVoice | null {
  if (voice === undefined || voice === null) voice = chooseVoice(s.getVoices());
  return voice;
}

/** Voices load late on some browsers; forget the choice when the list changes. */
function watchVoices(s: SpeechSynthesis) {
  s.addEventListener?.("voiceschanged", () => {
    voice = undefined;
  });
}

let unlocked = false;

/** Call from a tap. Speaks an empty utterance so later speech is allowed on iPadOS. */
export function unlockSpeech(): void {
  const s = synth();
  if (!s || unlocked) return;
  unlocked = true;
  watchVoices(s);
  try {
    const u = new SpeechSynthesisUtterance("");
    u.volume = 0;
    s.speak(u);
  } catch {
    /* no speech on this device */
  }
}

/** Speak `text`, interrupting anything still being said. Silent if no English voice. */
export function speak(text: string): void {
  const s = synth();
  if (!s) return;
  try {
    const v = currentVoice(s);
    if (!v) return;
    s.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.voice = v;
    u.lang = v.lang;
    u.rate = 0.95;
    s.speak(u);
  } catch {
    /* speech is a nice-to-have; never break the round */
  }
}

export function stopSpeaking(): void {
  try {
    synth()?.cancel();
  } catch {
    /* ignore */
  }
}
